/**
 * Sentry Error Tracking Service - Frontend
 *
 * Serviço centralizado para monitoramento de erros e performance no frontend.
 * Integra Sentry para captura automática de exceções, performance tracking,
 * e user feedback.
 *
 * Features:
 * - Captura automática de exceções não tratadas
 * - Performance monitoring (Web Vitals)
 * - Error boundaries integration
 * - User feedback widget
 * - Session replay (opcional)
 *
 * @module services/sentryService
 */

import * as Sentry from '@sentry/react';

/**
 * Configuração do Sentry para React
 */
class SentryService {
    private initialized = false;

    /**
     * Inicializa o Sentry com configurações do ambiente
     */
    initialize(): void {
        const dsn = import.meta.env.VITE_SENTRY_DSN;
        const environment = import.meta.env.MODE || 'development';

        // Só inicializa se DSN estiver configurado
        if (!dsn) {
            console.log('ℹ️  Sentry DSN não configurado. Error tracking desabilitado.');
            return;
        }

        // Não inicializar em desenvolvimento local
        if (environment === 'development' && !import.meta.env.VITE_SENTRY_ENABLED) {
            console.log('ℹ️  Sentry desabilitado em desenvolvimento local.');
            return;
        }

        try {
            Sentry.init({
                dsn,
                environment,

                // Performance Monitoring
                tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% em prod, 100% em staging

                // Integrations - Sentry v8+ usa browserTracingIntegration
                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.replayIntegration(),
                ],

                // Filtros de PII (dados sensíveis)
                beforeSend: (event) => {
                    // Remove dados sensíveis do localStorage/sessionStorage
                    if (event.breadcrumbs) {
                        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
                            if (breadcrumb.data && typeof breadcrumb.data === 'object') {
                                const data = breadcrumb.data as Record<string, unknown>;

                                // Remove tokens e senhas
                                if (data.token) data.token = '[REDACTED]';
                                if (data.password) data.password = '[REDACTED]';
                                if (data.accessToken) data.accessToken = '[REDACTED]';
                            }
                            return breadcrumb;
                        });
                    }

                    // Remove dados sensíveis de form submissions
                    if (event.request?.data) {
                        const data = event.request.data as Record<string, unknown>;

                        const sensitiveFields = ['password', 'token', 'apiKey', 'cpf', 'cnpj', 'creditCard'];
                        sensitiveFields.forEach(field => {
                            if (data && field in data) {
                                data[field] = '[REDACTED]';
                            }
                        });
                    }

                    return event;
                },

                // Ignora certos erros comuns
                ignoreErrors: [
                    // Erros de rede comuns (transientes)
                    'NetworkError',
                    'Network request failed',
                    'Failed to fetch',

                    // Erros de browser extensions
                    'ResizeObserver loop limit exceeded',
                    'Non-Error promise rejection captured',

                    // Erros de validação (esperados)
                    'ValidationError',
                ],

                // Configurações de envio
                maxBreadcrumbs: 50,
                maxValueLength: 1000,
            });

            this.initialized = true;
            console.log(`✅ Sentry frontend inicializado (environment: ${environment})`);
        } catch (error) {
            console.error('❌ Erro ao inicializar Sentry:', error);
        }
    }

    /**
     * Verifica se o Sentry está disponível
     */
    isAvailable(): boolean {
        return this.initialized;
    }

    /**
     * Captura uma exceção manualmente
     *
     * @param error - Erro a ser capturado
     * @param context - Contexto adicional
     */
    captureException(error: Error | unknown, context?: Record<string, unknown>): void {
        if (!this.initialized) {
            console.error('Sentry error:', error);
            return;
        }

        if (context) {
            Sentry.withScope(scope => {
                Object.entries(context).forEach(([key, value]) => {
                    scope.setContext(key, value as Record<string, unknown>);
                });
                Sentry.captureException(error);
            });
        } else {
            Sentry.captureException(error);
        }
    }

    /**
     * Captura uma mensagem manualmente
     *
     * @param message - Mensagem a ser capturada
     * @param level - Nível de severidade
     * @param context - Contexto adicional
     */
    captureMessage(
        message: string,
        level: Sentry.SeverityLevel = 'info',
        context?: Record<string, unknown>
    ): void {
        if (!this.initialized) {
            console.log(`Sentry message [${level}]:`, message);
            return;
        }

        if (context) {
            Sentry.withScope(scope => {
                Object.entries(context).forEach(([key, value]) => {
                    scope.setContext(key, value as Record<string, unknown>);
                });
                Sentry.captureMessage(message, level);
            });
        } else {
            Sentry.captureMessage(message, level);
        }
    }

    /**
     * Define contexto do usuário
     *
     * @param user - Dados do usuário
     */
    setUser(user: { id: string; email?: string; role?: string }): void {
        if (!this.initialized) return;

        Sentry.setUser({
            id: user.id,
            email: user.email,
            role: user.role,
        });
    }

    /**
     * Remove contexto do usuário
     */
    clearUser(): void {
        if (!this.initialized) return;
        Sentry.setUser(null);
    }

    /**
     * Adiciona tags customizadas
     *
     * @param tags - Tags a serem adicionadas
     */
    setTags(tags: Record<string, string>): void {
        if (!this.initialized) return;

        Object.entries(tags).forEach(([key, value]) => {
            Sentry.setTag(key, value);
        });
    }

    /**
     * Adiciona contexto customizado
     *
     * @param name - Nome do contexto
     * @param data - Dados do contexto
     */
    setContext(name: string, data: Record<string, unknown>): void {
        if (!this.initialized) return;
        Sentry.setContext(name, data);
    }

    /**
     * Adiciona breadcrumb (rastro de navegação)
     *
     * @param breadcrumb - Breadcrumb a ser adicionado
     */
    addBreadcrumb(breadcrumb: {
        message: string;
        category?: string;
        level?: Sentry.SeverityLevel;
        data?: Record<string, unknown>;
    }): void {
        if (!this.initialized) return;
        Sentry.addBreadcrumb(breadcrumb);
    }

    /**
     * Mostra widget de feedback do usuário (quando houver erro)
     *
     * @param eventId - ID do evento Sentry
     */
    showReportDialog(eventId?: string): void {
        if (!this.initialized) return;

        Sentry.showReportDialog({
            eventId,
            title: 'Algo deu errado!',
            subtitle: 'Nossa equipe foi notificada.',
            subtitle2: 'Se você quiser nos ajudar, conte-nos o que aconteceu abaixo.',
            labelName: 'Nome',
            labelEmail: 'Email',
            labelComments: 'O que aconteceu?',
            labelClose: 'Fechar',
            labelSubmit: 'Enviar',
            errorGeneric: 'Ocorreu um erro ao enviar seu feedback. Por favor, tente novamente.',
            errorFormEntry: 'Alguns campos são inválidos. Por favor, corrija os erros e tente novamente.',
            successMessage: 'Seu feedback foi enviado. Obrigado!',
        });
    }

    /**
     * Obtém client Sentry nativo (para uso avançado)
     */
    getClient(): typeof Sentry {
        return Sentry;
    }

    /**
     * Retorna componente ErrorBoundary do Sentry
     */
    getErrorBoundary(): typeof Sentry.ErrorBoundary {
        return Sentry.ErrorBoundary;
    }
}

// Exporta instância singleton
export default new SentryService();
